import { useState, useCallback } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { formatCurrency } from "../../lib/formatters";
const STATUS_LABELS = {
  lost: { label: "Lost", color: "bg-yellow-100 text-yellow-800" },
  stolen: { label: "Stolen", color: "bg-red-100 text-red-800" },
  damaged: { label: "Damaged", color: "bg-orange-100 text-orange-800" },
};

const EMPTY_FORM = { description: "", cost: "", status: "lost", date: new Date().toISOString().split("T")[0], notes: "" };

const ToolLossCard = ({ job, getAuthHeaders, onJobUpdate }) => {
  const losses = job.tool_losses || [];
  const totalLosses = job.total_tool_losses || 0;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const openAdd = useCallback(() => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((loss) => {
    setEditingId(loss.id);
    setForm({ description: loss.description, cost: loss.cost.toString(), status: loss.status, date: loss.date, notes: loss.notes || "" });
    setDialogOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!form.description.trim() || !form.cost) {
      toast.error("Description and cost are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { description: form.description.trim(), cost: parseFloat(form.cost), status: form.status, date: form.date, notes: form.notes.trim() };
      let res;
      if (editingId) {
        res = await apiClient.put(`/jobs/${job.id}/tool-losses/${editingId}`, payload);
      } else {
        res = await apiClient.post(`/jobs/${job.id}/tool-losses`, payload);
      }
      if (onJobUpdate) onJobUpdate();
      setDialogOpen(false);
      toast.success(editingId ? "Entry updated" : "Loss logged");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [form, editingId, job.id, getAuthHeaders, onJobUpdate]);

  const handleDelete = useCallback(async (lossId) => {
    try {
      const res = await apiClient.delete(`/jobs/${job.id}/tool-losses/${lossId}`);
      if (onJobUpdate) onJobUpdate();
      toast.success("Entry removed (tool recovered)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  }, [job.id, getAuthHeaders, onJobUpdate]);

  return (
    <Card data-testid="tool-loss-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Tool &amp; Equipment Losses
            {totalLosses > 0 && (
              <span className="text-sm font-normal text-red-600 ml-2">
                ({formatCurrency(totalLosses)} total)
              </span>
            )}
          </CardTitle>
          <Button size="sm" onClick={openAdd} data-testid="add-tool-loss-btn">
            <Plus className="w-4 h-4 mr-1" /> Log Loss
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {losses.length === 0 ? (
          <p className="text-muted-foreground text-center py-6" data-testid="no-tool-losses">No losses recorded</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {losses.map((loss) => {
                const status = STATUS_LABELS[loss.status] || STATUS_LABELS.lost;
                return (
                  <TableRow key={loss.id} data-testid={`tool-loss-row-${loss.id}`}>
                    <TableCell className="whitespace-nowrap">{loss.date}</TableCell>
                    <TableCell>{loss.description}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600">{formatCurrency(loss.cost, true)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">{loss.notes}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(loss)} data-testid={`edit-loss-${loss.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" data-testid={`delete-loss-${loss.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove Loss Entry</AlertDialogTitle>
                              <AlertDialogDescription>
                                Remove "{loss.description}" ({formatCurrency(loss.cost, true)}) from the loss log? Use this if the tool was found or recovered.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(loss.id)}>Remove</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Loss Entry" : "Log Tool/Equipment Loss"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>Description</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="e.g., DeWalt Table Saw"
                  data-testid="tool-loss-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cost ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.cost}
                    onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))}
                    placeholder="0.00"
                    data-testid="tool-loss-cost"
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                    <SelectTrigger data-testid="tool-loss-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lost">Lost</SelectItem>
                      <SelectItem value="stolen">Stolen</SelectItem>
                      <SelectItem value="damaged">Damaged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  data-testid="tool-loss-date"
                />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Any additional details..."
                  rows={2}
                  data-testid="tool-loss-notes"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} data-testid="save-tool-loss-btn">
                  {saving ? "Saving..." : editingId ? "Update" : "Log Loss"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ToolLossCard;
